import { safeRealm } from "../utils/safe-realm";
import type { ExpressionNodeQueryType } from "@intelligems/ig-types/dtos/audiences";
import type { PluginTrafficMatch } from "@intelligems/ig-types/schemas/plugin/plugin/utils";
import type { UrlMatch } from "@intelligems/ig-types/dtos/url-match";
import { fromProcess } from "@intelligems/ig-utils/env";

export const injectStyles = (styles: string) => {
  const style = document.createElement("style");
  style.innerHTML = styles;
  document.head.appendChild(style);
};

export const safeRemoveQueryParam = (key: string): void => {
  const url = new URL(window.location.href); // Parse the current URL
  const params = url.searchParams; // Access the query parameters

  if (params.has(key)) {
    params.delete(key); // Remove the specified query parameter
    safeRealm.replaceUrl(url.toString()); // Update the URL in the browser
  }
};

export async function HMAC(message: string, key: string) {
  if (fromProcess("NODE_ENV", undefined) === "test") {
    const Base64 = await import("crypto-js/enc-base64");
    const HmacSHA512 = await import("crypto-js/hmac-sha512");
    const bodyStr = message;
    const hmac = HmacSHA512.default(bodyStr, key);
    return Base64.default.stringify(hmac);
  } else {
    const g = (str: string) =>
        new Uint8Array(
          [...unescape(encodeURIComponent(str))].map((c) => c.charCodeAt(0))
        ),
      k = g(key),
      m = g(message),
      c = await window.crypto.subtle.importKey(
        "raw",
        k,
        { name: "HMAC", hash: "SHA-512" },
        true,
        ["sign"]
      ),
      s = await window.crypto.subtle.sign("HMAC", c, m);
    return btoa(String.fromCharCode(...new Uint8Array(s)));
  }
}

const periodRegex = /\./g;

/**
 * Extracts a view query parameter from a template string path.
 *
 * Function behavior:
 * 1. Removes "templates/" prefix if present
 * 2. For paths with multiple periods (e.g. "templates/foo.bar.baz.json"):
 *    - Returns middle segments between first and last (e.g. "bar.baz")
 * 3. For paths with 0-1 periods (e.g. "templates/foo.json" or "foo"):
 *    - Returns everything before the first period (e.g. "foo")
 *
 * @param template - Template path string (e.g. "templates/foo.bar.json")
 * @returns The extracted view query parameter
 */
export const getViewQueryParamFromTemplate = (template: string) => {
  template = template.replace("templates/", "");

  const periods = template.match(periodRegex);

  let search: string;

  if ((periods?.length || 0) > 1) {
    const templateParts = template.split(".");
    // slice(1, -1) returns array without first and last element
    search = templateParts.slice(1, -1).join(".");
    return search;
  } else {
    search = template;
    return search.split(".")[0];
  }
};

export const getSuffixFromTemplate = (template: string): string => {
  const periods = template.match(periodRegex) || [];
  let search: string;

  if (periods.length > 1) {
    const templateParts = template.split(".");
    // slice(1, -1) returns array without first and last element
    search = templateParts.slice(1, -1).join(".");
    return search;
  } else {
    // there is no suffix for the template
    return "";
  }
};

export const expectedString = (expected: string) => {
  const expectedList = expected.split(",");
  const diff = expectedList.length - 3;
  return expectedList.length > 3
    ? [
        ...expectedList.slice(0, 3),
        `or ${diff} other${diff > 1 ? "s" : ""}`,
      ].join(", ")
    : expectedList.join(", ");
};

export function getIdIndex(idHex: string, entityId: string): number {
  if (idHex == null || entityId == null) {
    throw new Error("Input parameters idHex and entityId must not be null.");
  }

  const combinedString = idHex + entityId;
  let hash = 0;
  const c1 = 0xcc9e2d51;
  const c2 = 0x1b873593;

  for (let i = 0; i < combinedString.length; i++) {
    let k1 = combinedString.charCodeAt(i);
    k1 = (k1 * c1) & 0xffffffff;
    k1 = (k1 << 15) | (k1 >>> 17); // ROTL32
    k1 = (k1 * c2) & 0xffffffff;

    hash ^= k1;
    hash = (hash << 13) | (hash >>> 19); // ROTL32
    hash = (hash * 5 + 0xe6546b64) & 0xffffffff;
  }

  hash ^= combinedString.length;
  hash ^= hash >>> 16;
  hash = (hash * 0x85ebca6b) & 0xffffffff;
  hash ^= hash >>> 13;
  hash = (hash * 0xc2b2ae35) & 0xffffffff;
  hash ^= hash >>> 16;

  return Math.abs(hash) % 100;
}

export function stringMatchEvaluation(options: {
  actual: string;
  value: string;
  matchBy?: UrlMatch | null;
}) {
  const { actual, matchBy, value } = options;

  switch (matchBy) {
    case "equals":
      return actual === value;
    case "startsWith":
      return actual.startsWith(value);
    case "endsWith":
      return actual.endsWith(value);
    case "doesNotEqual":
      return actual !== value;
    case "doesNotContain":
      return !actual.includes(value);
    case "doesNotStartWith":
      return !actual.startsWith(value);
    case "doesNotEndWith":
      return !actual.endsWith(value);
    case "isNull":
      return !actual;
    case "isNotNull":
      return !!actual;
    case "contains":
      return actual.includes(value);
    case "regex": {
      const regex = new RegExp(value);
      return regex.test(actual);
    }
    default:
      return false;
  }
}

export function filterEvaluation(options: {
  node: ExpressionNodeQueryType;
  actual: string;
  label?: string;
}): PluginTrafficMatch {
  const { actual, node, label } = options;
  const matched = stringMatchEvaluation({
    value: node.value,
    actual,
    matchBy: node.filter,
  });
  return {
    type: matched ? "success" : "failed",
    by: getExpressionMessage(getExpressionCondition(node, label), actual),
  };
}

export function getExpressionCondition(
  node: ExpressionNodeQueryType,
  label: string = node.type
) {
  switch (node.filter) {
    case "equals":
      return `${label} is ${node.value}`;
    case "startsWith":
      return `${label} starts with ${node.value}`;
    case "endsWith":
      return `${label} ends with ${node.value}`;
    case "doesNotEqual":
      return `${label} is not ${node.value}`;
    case "doesNotContain":
      return `${label} does not contain ${node.value}`;
    case "doesNotStartWith":
      return `${label} does not start with ${node.value}`;
    case "doesNotEndWith":
      return `${label} does not end with ${node.value}`;
    case "isNull":
      return `${label} is not defined`;
    case "isNotNull":
      return `${label} is defined`;
    case "contains":
      return `${label} contains ${node.value}`;
    case "regex":
      return `${label} matches regex ${node.value}`;
    default:
      return `${label} ${node.filter} ${node.value}`;
  }
}

export function getExpressionMessage(condition: string, actual: unknown) {
  return `${condition} &rarr; ${formatActual(actual)}`;
}

function formatActual(actual: unknown) {
  if (actual === "" || actual === null || actual === undefined) {
    return "&lt;undefined&gt;";
  } else {
    // eslint-disable-next-line @typescript-eslint/no-base-to-string
    return actual?.toString();
  }
}

export const isGoogleBot = () => {
  return (
    navigator.userAgent.indexOf("Googlebot") !== -1 ||
    navigator.userAgent.indexOf("AdsBot-Google") !== -1 ||
    navigator.userAgent.indexOf("Storebot-Google") !== -1
  );
};

export async function waitFor(
  condition: () => boolean,
  callback: () => void,
  options?: {
    duration?: number;
    interval?: number;
  }
) {
  if (condition()) {
    callback();
    return Promise.resolve();
  } else {
    const start = Date.now();
    const duration = options?.duration ?? 200;
    const interval = options?.interval ?? 5;
    return new Promise<void>((resolve) => {
      const intervalId = setInterval(() => {
        if (condition()) {
          clearInterval(intervalId);
          callback();
          resolve();
        } else if (Date.now() - start > duration) {
          clearInterval(intervalId);
          resolve();
        }
      }, interval);
    });
  }
}

let devMode: boolean | undefined = undefined;
declare const __NODE_ENV__: string;

export const isDevMode = () => {
  if (devMode !== undefined) {
    return devMode;
  }
  devMode = __NODE_ENV__ === "localhost";
  return devMode;
};
